import React, { useCallback } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useFormikDispatchSubmit, ThunkCreator } from 'hooks/useFormikSubmit';
import { ValidatingForm } from './ValidatingForm';

interface Props<Values> {
  children: React.ReactNode;
  initialValues: Values;
  validationSchema?: any | (() => any);
  submitCall: ThunkCreator<Values>;
  onSuccess?: (res: any, values: Values, helpers: FormikHelpers<Values>) => any;
  onSubmit?: (values: Values, helpers: FormikHelpers<Values>) => any;
  onError?: (
    e: Error,
    helpers: FormikHelpers<Values>,
    values: Values,
  ) =>
    | 'RETRY'
    | undefined
    | boolean
    | Promise<boolean | undefined | 'RETRY'>
    | void;
  updateValues?: boolean | ((res: any) => Values);
  noSuccessToast?: boolean;
  successToastMessage?: string;
  name?: string;
  className?: string;
  onValidationFailed?: (errors?: any) => any;
  showToastOnValidationFailed?: boolean;
}

export const SubmitForm = <Values extends {}>({
  children,
  initialValues,
  validationSchema,
  submitCall,
  onSuccess,
  className,
  onValidationFailed,
  showToastOnValidationFailed,
  ...useFormikSubmitOptions
}: Props<Values>) => {
  // Change the default api for more intuitive one
  const _onSuccess = useCallback(
    (helpers: FormikHelpers<Values>, values: Values, res: any) => {
      if (!onSuccess) {
        return;
      }
      return onSuccess(res, values, helpers);
    },
    [onSuccess], //eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSubmit = useFormikDispatchSubmit(submitCall, {
    ...useFormikSubmitOptions,
    onSuccess: _onSuccess,
  });
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <ValidatingForm
        className={className}
        onValidationFailed={onValidationFailed}
        showToast={showToastOnValidationFailed}
      >
        {children}
      </ValidatingForm>
    </Formik>
  );
};
